import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import User from "@mapmycustomers/shared/types/User";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import PreviewDealsListModal from "@app/component/PreviewDealsListModal";
import { showEntityView } from "@app/store/entityView/actions";

import useDealListColumns from "./util/useDealListColumns";

export interface Props {
  funnelName?: Funnel["name"];
  onHide: () => void;
  onShowEntityView: typeof showEntityView;
  stage: Stage;
  userIds: User["id"][];
}

const DealListModalForClosedStages: React.FC<Props> = ({
  funnelName,
  onHide,
  onShowEntityView,
  stage,
  userIds,
}) => {
  const intl = useIntl();

  const handleCompanyClick = useCallback(
    (entityId: Company["id"]) => onShowEntityView?.({ entityId, entityType: EntityType.COMPANY }),
    [onShowEntityView]
  );

  const handlePersonClick = useCallback(
    (entityId: Person["id"]) => onShowEntityView?.({ entityId, entityType: EntityType.PERSON }),
    [onShowEntityView]
  );

  const columns = useDealListColumns(handleCompanyClick, handlePersonClick, stage.type);

  const modalStageName = useMemo(() => {
    if (stage.type === DealStageType.WON) {
      return intl.formatMessage({
        id: "dealListModal.title.dealWon",
        defaultMessage: "Deals Won",
        description: "Deals Won stage name for deal list modal for closed deals",
      });
    }
    if (stage.type === DealStageType.LOST) {
      return intl.formatMessage({
        id: "dealListModal.title.dealLost",
        defaultMessage: "Deals Lost",
        description: "Deals Lost stage name for deal list modal for closed deals",
      });
    }
    return undefined;
  }, [intl, stage]);

  const handleDealClick = useCallback(
    (deal: Deal) => onShowEntityView({ entityId: deal.id, entityType: EntityType.DEAL }),
    [onShowEntityView]
  );
  const filter: FilterModel = useMemo(
    () => ({
      stage: { operator: FilterOperator.EQUALS, value: stage?.id },
      user: { operator: FilterOperator.IN_ANY, value: userIds },
    }),
    [userIds, stage.id]
  );

  return (
    <PreviewDealsListModal
      columns={columns}
      filter={filter}
      onDealClick={handleDealClick}
      onHide={onHide}
      stageName={modalStageName}
      subtitle={!modalStageName ? stage.name : funnelName}
      title={
        !modalStageName
          ? intl.formatMessage({
              id: "dealListModal.title",
              defaultMessage: "Deals Currently in Stage:",
              description: "Title of a deal list modal",
            })
          : undefined
      }
    />
  );
};

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(null, mapDispatchToProps)(DealListModalForClosedStages);
